/**
 * IdentifyStep — Step 1 of the Add-Building modal.
 *
 * Leo's locked-in design: single combined input, debounced lookup. The user
 * types an address, BBL, or BIN; we classify locally and the preview hook
 * fires when the input is valid.
 *
 * Validation before network:
 *   - min 5 chars
 *   - digits-only → must be exactly 10 (BBL) or 7 (BIN)
 *   - borough digit (first char of BBL/BIN) must be 1–5
 *
 * A11y:
 *   - `<Label htmlFor>` on the input
 *   - per-input error is announced via `aria-live="polite"`
 *   - the live status region under the input announces "Checking…" and
 *     "Found / Not found" transitions
 *
 * Sprint 7 § F2 — when `matches.length > 10` we render a `<CommandInput>`
 * filter at the top of the candidate list and group rows by address. Below
 * that threshold, we keep the flat list (the filter is just clutter at low
 * cardinality). The list container is `max-h-96 overflow-y-auto` so 100+
 * rows (Stuy Town has 39+) scroll cleanly without virtualization deps.
 *
 * Sprint 7 § F2 — when `matches.length === 1 && classified.kind === "bin"`
 * we auto-advance to Step 2. Memo 25 § "Auto-pick thresholds" — a 7-digit
 * BIN with one match is a high-confidence exact-key lookup; auto-advancing
 * saves a click. BBL or address with one match does NOT auto-advance —
 * those collapsed to a single row by accident, not by user intent.
 *
 * Sprint 7 § F3 — clicking a row whose `alreadyTrackedBuildingId` is non-null
 * routes to the existing building's dashboard and shows a friendly toast.
 * Avoids the 4-step-then-409 trap from `AddBuildingDialog.tsx:412–414`.
 */

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { CheckCircle2, Loader2, Search } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import { Badge } from "@/components/shared/ui/badge";
import { Button } from "@/components/shared/ui/button";
import {
  Command,
  CommandInput,
} from "@/components/shared/ui/command";
import { Input } from "@/components/shared/ui/input";
import { Label } from "@/components/shared/ui/label";
import Pill from "@/components/shared/ui/Pill";
import { toast } from "@/queries/useToast";
import { classifySearchTerm, useCblPreview } from "./useCblPreview";
import type { AddBuildingDraft, CblPreviewMatch } from "./types";

interface IdentifyStepProps {
  draft: AddBuildingDraft;
  onSearchTermChange: (value: string) => void;
  /**
   * Fire the CBL preview fetch for the current ``searchTerm``. Wired to
   * the Search button click AND Enter keypress in the input. Decouples
   * typing from network so the user controls when we hit the BE — see
   * useCblPreview docstring for the rationale.
   */
  onSubmitSearch: () => void;
  /**
   * Sprint 7 § F2: callers pass `candidateCount` so the dialog reducer can
   * record `wasAmbiguous` for D1's recovery banner. The count is the size of
   * the match list at the moment of pick (after any user filter, the FULL
   * unfiltered count is what matters for D1 — it's reporting on resolver
   * cardinality, not UI-state).
   */
  onSelectMatch: (match: CblPreviewMatch, candidateCount: number) => void;
  onCancel: () => void;
}

// Group-by-address threshold. Below this we render a flat list — any
// grouping below 10 rows costs more visual noise than it saves.
const GROUP_THRESHOLD = 10;

const IdentifyStep: React.FC<IdentifyStepProps> = ({
  draft,
  onSearchTermChange,
  onSubmitSearch,
  onSelectMatch,
  onCancel,
}) => {
  const inputId = useId();
  const errorId = useId();
  const statusId = useId();
  const term = draft.identify.searchTerm;
  // Default to "" when the field is absent on the draft — older test
  // fixtures may construct an identify object without it, and useCblPreview
  // would crash classifying ``undefined``.
  const submittedTerm = draft.identify.submittedTerm ?? "";
  const classified = classifySearchTerm(term);
  const { data, isFetching, isError } = useCblPreview(term, submittedTerm);
  const hasUnsubmittedEdits =
    submittedTerm !== "" && term.trim() !== submittedTerm;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const navigate = useNavigate();

  // F2: candidate-list filter. Reset whenever the search term changes so
  // a stale filter doesn't hide every match after the user retypes.
  const [filterText, setFilterText] = useState("");
  useEffect(() => {
    setFilterText("");
  }, [term]);

  // Focus the heading on mount so assistive tech reads the step title when
  // the modal opens or we back-navigate into this step.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const hasTerm = term.trim().length > 0;
  const inputError = hasTerm ? classified.error : null;
  const matches = data?.matches ?? [];
  const ambiguous = matches.length > 1;

  // F2 § auto-advance: BIN + 1 match. Guard:
  //   - kind must be "bin" (BBL/address single-match still requires a click)
  //   - the single match must NOT be already-tracked (we'd skip the F3 toast
  //     and route into a duplicate-add flow)
  // We do this in an effect so the dispatch happens after render and React
  // doesn't complain about state updates during render. The dialog's reducer
  // moves us to step "verifyCbl"; this component unmounts before any second
  // dispatch is possible.
  useEffect(() => {
    if (isFetching || isError) return;
    if (matches.length !== 1) return;
    if (classified.kind !== "bin") return;
    const sole = matches[0];
    if (sole.alreadyTrackedBuildingId) return;
    onSelectMatch(sole, 1);
    // We only need the dispatch once per data load. Including `onSelectMatch`
    // in deps would re-fire if the parent re-creates the callback identity,
    // but the dispatch itself navigates away so the duplicate fires before
    // unmount are harmless. Track the data identity instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, classified.kind, isFetching, isError]);

  // F3: tracked-row click. Navigate to the existing building's dashboard
  // and show a toast — never POST, never SELECT_MATCH (avoids the 4-step
  // /buildings/ 409). Untracked rows fall through to the dialog reducer.
  const handleRowClick = (match: CblPreviewMatch) => {
    if (match.alreadyTrackedBuildingId) {
      toast.info({
        heading: "Already in your portfolio",
        body: "We brought you to this building instead of adding a duplicate.",
      });
      navigate({
        to: "/buildings/$buildingId",
        params: { buildingId: match.alreadyTrackedBuildingId },
      });
      return;
    }
    onSelectMatch(match, matches.length);
  };

  let statusMessage: string | null = null;
  if (isFetching) {
    statusMessage = "Checking NYC Covered Buildings List...";
  } else if (isError) {
    statusMessage = "Something went wrong reaching NYC records. Try again.";
  } else if (hasUnsubmittedEdits) {
    statusMessage = `Press Search to update results for "${term}".`;
  } else if (!submittedTerm && hasTerm && !inputError) {
    statusMessage = "Press Search or hit Enter to look up this building.";
  } else if (data && matches.length === 0) {
    statusMessage = "No results.";
  } else if (data && matches.length === 1) {
    const m = matches[0];
    statusMessage = `Found ${m.identity.address ?? "a match"}.`;
  } else if (data && matches.length > 1) {
    statusMessage = `Found ${matches.length} possible matches. Pick one to continue.`;
  }

  const useFilterAndGroup = matches.length > GROUP_THRESHOLD;

  // Filter pass — case-insensitive substring across address, bin, bbl.
  const filteredMatches = useMemo(() => {
    if (!useFilterAndGroup) return matches;
    const needle = filterText.trim().toLowerCase();
    if (!needle) return matches;
    return matches.filter((m) => {
      const fields = [
        m.identity.address,
        m.identity.bin,
        m.identity.bbl,
      ];
      return fields.some(
        (f) => typeof f === "string" && f.toLowerCase().includes(needle),
      );
    });
  }, [matches, filterText, useFilterAndGroup]);

  // Group-by-address. Preserve insertion order so the backend-supplied
  // ordering (2026-CBL first, then delisted, then created_at — see B2 brief)
  // is respected. `Map` iteration is insertion-ordered in JS.
  const grouped = useMemo(() => {
    if (!useFilterAndGroup) return null;
    const m = new Map<string, CblPreviewMatch[]>();
    filteredMatches.forEach((match) => {
      const addr = match.identity.address ?? "(no address on file)";
      const bucket = m.get(addr);
      if (bucket) bucket.push(match);
      else m.set(addr, [match]);
    });
    return Array.from(m.entries());
  }, [filteredMatches, useFilterAndGroup]);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-lg font-semibold leading-tight focus:outline-none"
        >
          Find your building
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          We&apos;ll look it up against NYC&apos;s Covered Buildings List and
          DOF records. BBL or BIN is fastest.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor={inputId}>Building address or BBL / BIN</Label>
        <div className="flex items-stretch gap-2">
          <Input
            id={inputId}
            autoComplete="off"
            placeholder="334 Foster Ave, Brooklyn, NY — or 3065010013 — or 3170297"
            value={term}
            onChange={(e) => onSearchTermChange(e.target.value)}
            onKeyDown={(e) => {
              // Enter submits — matches the Search button. Guard on
              // valid input so we don't fire a doomed fetch.
              if (
                e.key === "Enter" &&
                !inputError &&
                term.trim().length >= 5 &&
                !isFetching
              ) {
                e.preventDefault();
                onSubmitSearch();
              }
            }}
            aria-invalid={Boolean(inputError)}
            aria-describedby={inputError ? errorId : statusId}
            className="flex-1"
            endIcon={
              isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Search className="h-4 w-4 text-muted-foreground" />
              )
            }
          />
          <Button
            type="button"
            onClick={onSubmitSearch}
            disabled={
              isFetching ||
              Boolean(inputError) ||
              term.trim().length < 5
            }
            data-testid="cbl-search-submit"
          >
            {isFetching ? "Searching…" : "Search"}
          </Button>
        </div>
        {inputError && (
          <p
            id={errorId}
            role="alert"
            aria-live="polite"
            className="text-sm text-danger"
          >
            {inputError}
          </p>
        )}
        <p
          id={statusId}
          aria-live="polite"
          className="text-sm text-muted-foreground min-h-[1.25rem]"
        >
          {statusMessage}
        </p>
      </div>

      {!isFetching && matches.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">
            {ambiguous
              ? "Pick the building you want to add:"
              : "Is this your building?"}
          </p>

          {useFilterAndGroup && (
            // `<Command shouldFilter={false}>` opts us out of cmdk's built-in
            // filtering — we drive the substring match ourselves and render
            // <button> rows (not <CommandItem>s) because the row layout has
            // <Pill> + <Badge> children that aren't part of cmdk's primitive.
            // We get the styled `<CommandInput>` visual + the keyboard-aware
            // input container without coupling to cmdk's selection model.
            <Command shouldFilter={false} className="border rounded-md">
              <CommandInput
                placeholder="Filter by street, BIN, or BBL"
                value={filterText}
                onValueChange={setFilterText}
                aria-label="Filter candidates"
              />
            </Command>
          )}

          <div className="max-h-96 overflow-y-auto">
            {useFilterAndGroup && grouped ? (
              <div className="flex flex-col gap-4">
                {grouped.length === 0 && (
                  <p className="text-sm text-muted-foreground py-3">
                    No candidates match &quot;{filterText}&quot;.
                  </p>
                )}
                {grouped.map(([address, rows]) => (
                  <div
                    key={address}
                    className="flex flex-col gap-1.5"
                  >
                    <h3 className="text-sm font-semibold text-muted-foreground sticky top-0 bg-background py-1">
                      {address}{" "}
                      <span className="font-normal text-xs">
                        ({rows.length}{" "}
                        {rows.length === 1 ? "building" : "buildings"})
                      </span>
                    </h3>
                    <ul className="flex flex-col gap-2">
                      {rows.map((match, idx) => (
                        <CandidateRow
                          key={
                            match.identity.bin ??
                            match.identity.bbl ??
                            `${match.outcome}-${address}-${idx}`
                          }
                          match={match}
                          onClick={handleRowClick}
                        />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {matches.map((match, idx) => {
                  const rowKey =
                    match.identity.bin ??
                    match.identity.bbl ??
                    `${match.outcome}-${idx}`;
                  return (
                    <CandidateRow
                      key={rowKey}
                      match={match}
                      onClick={handleRowClick}
                    />
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-row justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CandidateRow — extracted from F1 inline JSX so the grouped + flat lists
// share the exact same row layout. Devil's advocate: extracting a single-use
// component is over-engineering, but here it's used twice (grouped + flat
// branches) and the row carries non-trivial a11y wiring. Worth keeping in one
// place so the two branches can't drift.
// ---------------------------------------------------------------------------

interface CandidateRowProps {
  match: CblPreviewMatch;
  onClick: (match: CblPreviewMatch) => void;
}

const CandidateRow: React.FC<CandidateRowProps> = ({ match, onClick }) => {
  const { identity, alreadyTrackedBuildingId } = match;
  // Primary line: address, or BIN as a fallback so the user sees *something*
  // identifying the row (memo 25 § 9 — degraded shape).
  const primary =
    identity.address ??
    (identity.bin ? `BIN ${identity.bin}` : "(no address on file)");
  const isTracked = alreadyTrackedBuildingId !== null;
  return (
    <li>
      <button
        type="button"
        onClick={() => onClick(match)}
        aria-label={
          isTracked ? `${primary}. Already in your portfolio.` : primary
        }
        className="group w-full min-h-[56px] text-left rounded-md border border-border bg-background hover:bg-accent hover:text-accent-foreground transition-colors px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <div className="flex flex-row items-start justify-between gap-3">
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="font-semibold text-sm truncate">{primary}</div>
            <div className="flex flex-row flex-wrap items-center gap-1.5">
              {identity.bin && (
                <Badge variant="outline" className="font-mono">
                  BIN {identity.bin}
                </Badge>
              )}
              {identity.bbl && (
                <Badge variant="outline" className="font-mono">
                  BBL {identity.bbl}
                </Badge>
              )}
              {identity.buildingClass && (
                <Badge variant="neutral">{identity.buildingClass}</Badge>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {isTracked && (
              <Pill
                color="green"
                // a11y: color is reinforced by an icon + text label
                // so the signal isn't color-only (WCAG 1.4.1).
                className="inline-flex items-center gap-1"
              >
                <CheckCircle2
                  className="h-3.5 w-3.5"
                  aria-hidden="true"
                />
                <span>Already in your portfolio</span>
              </Pill>
            )}
            <span className="text-xs text-muted-foreground">
              {identity.yearBuilt ?? "—"}
            </span>
          </div>
        </div>
      </button>
    </li>
  );
};

export default IdentifyStep;
